import React, { useState } from 'react';
import { WizardHeader, JourneyRail, TipFromRobbie, SubStepPills, JourneyStep } from '../../components/wizard';
import Section from './Section';

/**
 * /design — wizard chrome v2, side by side with the
 * `RFQ Wizard v2*.dc.html` mocks: 52px navy header (Continue vs Award),
 * journey rail at step 1 and step 3 (sub-steps expanded), locked group,
 * Tip from Robbie slot, and the step-3 segmented pills in all three states.
 */

const LOCKED = ['Financing', 'Delivery', 'Installation', 'Contracting'];

const step1Steps: JourneyStep[] = [
  { id: 'upload', label: 'Upload BOM', state: 'active' },
  { id: 'criteria', label: 'Sourcing Criteria', state: 'upcoming' },
  { id: 'discovery', label: 'Supplier Discovery', state: 'upcoming' },
  { id: 'outreach', label: 'Supplier Outreach', state: 'upcoming' },
  { id: 'quotes', label: 'Quotes & Proposals', state: 'upcoming' },
];

const step3Steps: JourneyStep[] = [
  { id: 'upload', label: 'Upload BOM', state: 'done' },
  { id: 'criteria', label: 'Sourcing Criteria', state: 'done' },
  {
    id: 'discovery',
    label: 'Supplier Discovery',
    state: 'active',
    subSteps: [
      { id: 'context', label: 'Context', state: 'done' },
      { id: 'validation', label: 'Validation', state: 'active' },
      { id: 'discovery-sub', label: 'Discovery', state: 'upcoming' },
    ],
  },
  { id: 'outreach', label: 'Supplier Outreach', state: 'upcoming' },
  { id: 'quotes', label: 'Quotes & Proposals', state: 'upcoming' },
];

// Revisit scenario: the user stepped back to Discovery from Outreach. Reached
// steps whose work is incomplete render "visited" — numbered but clickable
// (never dead "upcoming"), with the reason on hover. Sub-steps already reached
// stay done/clickable too.
const revisitSteps: JourneyStep[] = [
  { id: 'upload', label: 'Upload BOM', state: 'done' },
  { id: 'criteria', label: 'Sourcing Criteria', state: 'visited', reason: 'Set your sourcing criteria' },
  {
    id: 'discovery',
    label: 'Supplier Discovery',
    state: 'active',
    subSteps: [
      { id: 'context', label: 'Context', state: 'done' },
      { id: 'validation', label: 'Validation', state: 'done' },
      { id: 'discovery-sub', label: 'Discovery', state: 'active' },
    ],
  },
  { id: 'outreach', label: 'Supplier Outreach', state: 'visited', reason: '2 shortlisted suppliers not contacted yet' },
  { id: 'quotes', label: 'Quotes & Proposals', state: 'upcoming', reason: 'Confirm an award plan' },
];

const WizardChromeGallery: React.FC = () => {
  const [pill, setPill] = useState('validation');
  const pills = [
    { id: 'context', label: 'Context', state: (pill === 'context' ? 'active' : 'done') as 'active' | 'done' },
    { id: 'validation', label: 'Validation', state: (pill === 'validation' ? 'active' : pill === 'discovery' ? 'done' : 'upcoming') as 'active' | 'done' | 'upcoming' },
    { id: 'discovery', label: 'Discovery', state: (pill === 'discovery' ? 'active' : 'upcoming') as 'active' | 'upcoming' },
  ];

  return (
    <Section title="Wizard chrome (header + journey rail)">
      <div className="flex flex-col gap-s6">
        <div className="overflow-hidden rounded-card border border-line">
          <WizardHeader
            notificationsSlot={null}
            name="Warehouse AMR Drive Subsystem"
            onBack={() => undefined}
            onRename={() => undefined}
            savedLabel="Saved just now"
            exportItems={[
              { id: 'sourcing-summary', label: 'Sourcing Summary (PDF)', onSelect: () => undefined },
              { id: 'smart-bom', label: 'Smart BOM (Excel)', onSelect: () => undefined },
            ]}
            onShare={() => undefined}
          />
        </div>
        {/* The same bar as a non-owner sees it: no Share, and no rename —
            an editor changes the RFQ, only the owner changes its guest list. */}
        <div className="overflow-hidden rounded-card border border-line">
          <WizardHeader
            notificationsSlot={null}
            name="Warehouse AMR Drive Subsystem"
            onBack={() => undefined}
            savedLabel="Saved just now"
            exportItems={[
              { id: 'sourcing-summary', label: 'Sourcing Summary (PDF)', onSelect: () => undefined },
              { id: 'smart-bom', label: 'Smart BOM (Excel)', onSelect: () => undefined, busy: true },
            ]}
          />
        </div>
        <div className="flex flex-wrap items-start gap-s6">
          <div className="h-[640px] w-step-rail overflow-hidden rounded-card border border-line">
            <JourneyRail
              steps={step1Steps}
              locked={LOCKED}
              tip={<TipFromRobbie>A detailed BOM helps me find the best suppliers faster.</TipFromRobbie>}
            />
          </div>
          <div className="h-[640px] w-step-rail overflow-hidden rounded-card border border-line">
            <JourneyRail
              steps={step3Steps}
              locked={LOCKED}
              tip={
                <TipFromRobbie>
                  These answers apply to the whole BOM — 30 seconds here saves a day of supplier back-and-forth.
                </TipFromRobbie>
              }
            />
          </div>
          <div className="h-[640px] w-step-rail overflow-hidden rounded-card border border-line">
            <JourneyRail steps={revisitSteps} locked={LOCKED} />
          </div>
          <div className="flex flex-col gap-s4">
            <span className="text-meta text-ink-muted">Step-3 segmented pills (click to move):</span>
            <SubStepPills items={pills} onSelect={setPill} />
          </div>
        </div>
      </div>
    </Section>
  );
};

export default WizardChromeGallery;
